import { motion } from 'framer-motion';
import { Mail, MessageCircle, Instagram, Music2, ExternalLink } from 'lucide-react';

const ContactPage = () => {
  return (
    <div className="contact-page" data-testid="contact-page">
      <div className="contact-container">
        <motion.div
          className="contact-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="contact-tag">Get in Touch</span>
          <h1>Contact Me</h1>
          <p>Got a question, feedback, or just want to say hi? I'd love to hear from you.</p>
        </motion.div>

        <div className="contact-cards">
          <motion.a
            href="mailto:BlackGirlTradingAcademy@hotmail.com"
            className="contact-card email-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="contact-email-card"
          >
            <div className="card-icon">
              <Mail size={32} />
            </div>
            <h2>Email Me</h2>
            <p>For any questions, support, or collaboration enquiries:</p>
            <span className="card-value">BlackGirlTradingAcademy@hotmail.com</span>
            <span className="card-cta">Click to send email <ExternalLink size={14} /></span>
          </motion.a>

          <motion.a
            href="https://www.tiktok.com/@BlackGirlsTrade_"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card tiktok-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="contact-tiktok-card"
          >
            <div className="card-icon">
              <Music2 size={32} />
            </div>
            <h2>TikTok</h2>
            <p>Follow my trading journey and daily content:</p>
            <span className="card-value">@BlackGirlsTrade_</span>
            <span className="card-cta">Follow on TikTok <ExternalLink size={14} /></span>
          </motion.a>

          <motion.a
            href="https://www.instagram.com/BlackGirlsTrade_"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card instagram-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="contact-instagram-card"
          >
            <div className="card-icon">
              <Instagram size={32} />
            </div>
            <h2>Instagram</h2>
            <p>Updates, stories, and behind the scenes:</p>
            <span className="card-value">@BlackGirlsTrade_</span>
            <span className="card-cta">Follow on Instagram <ExternalLink size={14} /></span>
          </motion.a>
        </div>

        <motion.div
          className="contact-note"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <MessageCircle size={20} />
          <p>I usually respond within 24-48 hours. For the quickest reply, email is best!</p>
        </motion.div>
      </div>

      <style>{`
        .contact-page {
          min-height: 100vh;
          padding: 80px 24px 60px;
        }
        .contact-container {
          max-width: 900px;
          margin: 0 auto;
        }
        .contact-header {
          text-align: center;
          margin-bottom: 48px;
        }
        .contact-tag {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(0, 255, 148, 0.1);
          border: 1px solid rgba(0, 255, 148, 0.2);
          border-radius: 20px;
          color: var(--primary);
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 20px;
        }
        .contact-header h1 {
          font-size: 2.5rem;
          margin: 0 0 12px;
        }
        .contact-header p {
          color: var(--text-muted);
          font-size: 1.1rem;
          max-width: 500px;
          margin: 0 auto;
        }
        .contact-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }
        .contact-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 36px 24px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          text-decoration: none;
          color: inherit;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .contact-card:hover {
          transform: translateY(-4px);
          border-color: var(--primary);
          box-shadow: 0 12px 40px rgba(0, 255, 148, 0.1);
        }
        .tiktok-card:hover { border-color: #ff0050; box-shadow: 0 12px 40px rgba(255, 0, 80, 0.1); }
        .instagram-card:hover { border-color: #e1306c; box-shadow: 0 12px 40px rgba(225, 48, 108, 0.1); }
        .card-icon {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
          margin-bottom: 20px;
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
        }
        .tiktok-card .card-icon { background: rgba(255, 0, 80, 0.1); color: #ff0050; }
        .instagram-card .card-icon { background: rgba(225, 48, 108, 0.1); color: #e1306c; }
        .contact-card h2 {
          font-size: 1.2rem;
          margin: 0 0 8px;
          color: var(--text-primary);
        }
        .contact-card p {
          font-size: 14px;
          color: var(--text-muted);
          margin: 0 0 16px;
          line-height: 1.5;
        }
        .card-value {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          word-break: break-all;
          margin-bottom: 16px;
        }
        .card-cta {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: var(--primary);
          margin-top: auto;
        }
        .tiktok-card .card-cta { color: #ff0050; }
        .instagram-card .card-cta { color: #e1306c; }
        .contact-note {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          background: rgba(0, 255, 148, 0.05);
          border: 1px solid rgba(0, 255, 148, 0.1);
          border-radius: 12px;
          max-width: 600px;
          margin: 0 auto;
        }
        .contact-note svg { color: var(--primary); flex-shrink: 0; }
        .contact-note p { margin: 0; font-size: 14px; color: var(--text-muted); }
        @media (max-width: 768px) {
          .contact-cards { grid-template-columns: 1fr; }
          .contact-header h1 { font-size: 1.8rem; }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
